import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { TicketTypeStore, type TicketKind } from "./ticket-types-store";
import { listTicketGroups, createTicketGroup, updateTicketGroup, deleteTicketGroup, duplicateTicketGroup } from "@v0y4ge/api-client";
import { logUpsert, logDelete } from "../../lib/audit";
import { t as i18nT } from "../../lib/i18n";

/**
 * Ticket Groups — reusable bundles of tiers, each tier carrying a comms flow.
 * Wired to the REAL kit ops (listTicketGroups / create / update / delete /
 * duplicate, imported above) with an empty-list fallback — the old "fictional
 * seed / no contract" note was stale (CC GM14 §3, minor item: comment only,
 * the code was already clean). `rev` is the version stamp attached events read.
 */
export type FlowStepType = "invitation" | "confirmation" | "receipt" | "reminder" | "thankyou";
export interface FlowStep { id: string; type: FlowStepType; channel: "email" | "sms"; timing: string; subject: string; on: boolean; }
export interface TicketTier { id: string; name: string; price: number; kind: TicketKind; color: string; desc: string; defaultAllocation: number; flow: FlowStep[]; typeId?: string; }
export interface TicketGroup { id: string; name: string; description: string; usedBy: number; rev?: number; tiers: TicketTier[]; _new?: boolean; }

export const TG_PALETTE = [T.tDarkBlue, T.tPurple, T.tPink, T.tOrange, T.tGreen, T.tTurquoise, T.tLightBlue, T.tRed];
export const TG_STEP_TYPES: Record<FlowStepType, { label: string; labelKey: string; icon: string; blurb: string }> = {
  invitation: { labelKey: "tickets.groupsStore.invitation", get label() { return i18nT("tickets.groupsStore.invitation", "Invitation"); }, icon: "send", blurb: "Personal invite with a register link" },
  confirmation: { labelKey: "tickets.groupsStore.confirmation", get label() { return i18nT("tickets.groupsStore.confirmation", "Confirmation"); }, icon: "check", blurb: "Booking confirmed + calendar invite" },
  receipt: { labelKey: "tickets.groupsStore.receipt", get label() { return i18nT("tickets.groupsStore.receipt", "Receipt"); }, icon: "ticket", blurb: "Payment receipt / order summary" },
  reminder: { labelKey: "tickets.groupsStore.reminder", get label() { return i18nT("tickets.groupsStore.reminder", "Reminder"); }, icon: "clock", blurb: "Pre-event nudge with the essentials" },
  thankyou: { labelKey: "tickets.groupsStore.thankYou", get label() { return i18nT("tickets.groupsStore.thankYou", "Thank-you"); }, icon: "star", blurb: "Follow-up + feedback request" },
};
export const TG_TIMINGS = [
  "When invited", "On registration", "On payment",
  "1 week before", "3 days before", "1 day before", "On event day", "1 hour before",
  "1 day after", "3 days after", "1 week after",
];
const TG_DEFAULT_TIMING: Record<FlowStepType, string> = {
  invitation: "When invited", confirmation: "On registration", receipt: "On payment",
  reminder: "1 day before", thankyou: "1 day after",
};

let _tgSeq = 0;
export const tgUid = (p: string) => `${p}_${Date.now().toString(36)}${(_tgSeq++).toString(36)}`;

export function tgStep(type: FlowStepType, over: Partial<FlowStep> = {}): FlowStep {
  return { id: tgUid(type), type, channel: "email", timing: TG_DEFAULT_TIMING[type] || "On registration", subject: "", on: true, ...over };
}
export function tgDefaultFlow(kind: TicketKind): FlowStep[] {
  if (kind === "paid") return [tgStep("confirmation"), tgStep("receipt"), tgStep("reminder"), tgStep("thankyou")];
  if (kind === "invite") return [tgStep("invitation"), tgStep("confirmation"), tgStep("reminder"), tgStep("thankyou")];
  return [tgStep("confirmation"), tgStep("reminder"), tgStep("thankyou")];
}
export function tgTier(over: Partial<TicketTier> = {}): TicketTier {
  const kind = over.kind || "free";
  return { id: tgUid("tier"), name: "", price: 0, kind, color: TG_PALETTE[0], desc: "", defaultAllocation: 50, flow: tgDefaultFlow(kind), ...over };
}

export const TicketGroupStore = (() => {
  let _groups: TicketGroup[] | null = null;
  let _hydrated = false;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());
  const hydrate = () => {
    if (_hydrated) return;
    _hydrated = true;
    listTicketGroups({} as any).then((r: any) => {
      const arr = r.groups ?? r.ticketGroups ?? r.data ?? [];
      _groups = arr.map((g: any): TicketGroup => ({
        id: g.id, name: g.name ?? "", description: g.description ?? "",
        usedBy: g.usedBy ?? 0, rev: g.rev ?? 1,
        tiers: (g.tiers ?? g.ticketTypes ?? []).map((t: any): TicketTier => ({
          id: t.id, name: t.name ?? "", price: t.price ?? 0, kind: (t.kind ?? "free") as TicketKind,
          color: t.color ?? TG_PALETTE[0], desc: t.desc ?? t.description ?? "",
          defaultAllocation: t.defaultAllocation ?? 50,
          flow: (t.flow ?? tgDefaultFlow(t.kind ?? "free")),
          typeId: t.typeId ?? undefined,
        })),
      }));
      notify();
    }).catch(() => { if (!_hydrated || _groups === null) { _groups = []; notify(); } });
  };
  const load = (): TicketGroup[] => { if (_groups === null) { _groups = []; hydrate(); } return _groups.slice(); };
  type AnyFn = (...a: unknown[]) => Promise<unknown>;
  const _op = (fn: unknown): AnyFn | null => (typeof fn === "function" ? fn as AnyFn : null);
  return {
    all: (): TicketGroup[] => load().slice(),
    get: (id: string): TicketGroup | null => load().find((g) => g.id === id) || null,
    // Awaited client write first; the in-memory cache + notify update only on
    // success (no-fake-interactivity mandate — the old notify-only save() is gone).
    upsert: async (g: TicketGroup): Promise<TicketGroup> => {
      load();
      const exists = _groups!.some((x) => x.id === g.id);
      if (exists) await _op(updateTicketGroup)?.(g.id, g);
      else await _op(createTicketGroup)?.(g);
      const prev = exists ? _groups!.find((x) => x.id === g.id) : null;
      logUpsert("ticketGroup", prev ? (prev as unknown as Record<string, unknown>) : null, g as unknown as Record<string, unknown>, { name: g.name });
      g.rev = Date.now();
      const i = _groups!.findIndex((x) => x.id === g.id);
      if (i >= 0) _groups![i] = g; else _groups = [g, ..._groups!];
      notify();
      return g;
    },
    remove: async (id: string) => {
      load();
      await _op(deleteTicketGroup)?.(id);
      const prev = _groups!.find((g) => g.id === id);
      if (prev) logDelete("ticketGroup", prev as unknown as Record<string, unknown>, { name: prev.name });
      _groups = _groups!.filter((g) => g.id !== id);
      notify();
    },
    duplicate: async (id: string): Promise<TicketGroup | null> => {
      load();
      const g = _groups!.find((x) => x.id === id); if (!g) return null;
      const copy: TicketGroup = JSON.parse(JSON.stringify(g));
      copy.id = tgUid("tg"); copy.name = g.name + " (copy)"; copy.usedBy = 0; copy.rev = Date.now();
      copy.tiers.forEach((t) => { t.id = tgUid("tier"); t.flow.forEach((s) => (s.id = tgUid(s.type))); });
      const res = await _op(duplicateTicketGroup)?.(id) as Record<string, any> | undefined;
      if (res?.id || res?.group?.id) copy.id = String(res.id ?? res.group.id);
      _groups = [copy, ..._groups!]; notify(); return copy;
    },
    subscribe: (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
    reset: () => { _groups = []; _hydrated = false; hydrate(); },
  };
})();

export function useTicketGroups(): TicketGroup[] {
  const [, force] = useState(0);
  useEffect(() => { const unsub = TicketGroupStore.subscribe(() => force((n) => n + 1)); return () => { unsub(); }; }, []);
  return TicketGroupStore.all();
}

export const tgInput = (extra: React.CSSProperties = {}): React.CSSProperties => ({ width: "100%", height: 40, padding: "0 12px", boxSizing: "border-box", border: `1px solid ${T.n4}`, borderRadius: 6, fontFamily: T.font, fontSize: 14, color: T.n1, background: T.white, outline: "none", ...extra });
export const tgMoney = (n: number) => (Number(n) ? `$${Number(n).toLocaleString()}` : "Free");
export function tgPriceLabel(group: TicketGroup): string {
  const prices = group.tiers.map((t) => (t.kind === "free" || t.kind === "invite") ? 0 : Number(t.price) || 0);
  const paid = prices.filter((p) => p > 0);
  if (!paid.length) return "Free";
  const min = Math.min(...paid);
  return prices.some((p) => p === 0) || paid.length > 1 ? `From $${min.toLocaleString()}` : `$${min.toLocaleString()}`;
}
export const tgFlowCount = (tier: TicketTier) => (tier.flow || []).filter((s) => s.on).length;
export const tgKindPill = (kind: TicketKind): { tone: "primary" | "warn" | "neutral"; label: string } =>
  kind === "paid" ? { tone: "primary", label: i18nT("tickets.groupsStore.paid", "Paid") } : kind === "invite" ? { tone: "warn", label: i18nT("tickets.groupsStore.inviteOnly", "Invite-only") } : { tone: "neutral", label: i18nT("tickets.groupsStore.free", "Free") };

export function tgNewGroup(): TicketGroup {
  // ui-config — new-group default shape (empty name; one default tier label)
  return { _new: true, id: tgUid("tg"), name: "", description: "", usedBy: 0, tiers: [tgTier({ name: "General admission", kind: "free", price: 0, color: TG_PALETTE[0] })] };
}

/** Refresh type-sourced tiers from the current Ticket Type definition. */
export function tgSyncTiersFromTypes(group: TicketGroup): TicketGroup {
  if (!group || !group.tiers) return group;
  group.tiers = group.tiers.map((t) => {
    if (!t.typeId) return t;
    const type = TicketTypeStore.get(t.typeId);
    if (!type) return t;
    return { ...t, name: type.name, kind: type.kind, price: type.price || 0, color: type.color, desc: type.desc || "" };
  });
  return group;
}

/** Flatten attached groups → per-event ticket rows (used by the wizard Tickets step).
 *  Carries each tier's defaultAllocation as `capacity` (additive) so attached
 *  groups contribute their allocations to the event capacity total. */
export function tgFlattenToTickets(attached: TicketGroup[]): Array<{ id: string; name: string; price: number; kind: TicketKind; color: string; groupId: string; groupName: string; capacity: number }> {
  const out: Array<{ id: string; name: string; price: number; kind: TicketKind; color: string; groupId: string; groupName: string; capacity: number }> = [];
  (attached || []).forEach((g) => {
    (g.tiers || []).forEach((t) => out.push({ id: t.id, name: t.name, price: t.price, kind: t.kind, color: t.color, groupId: g.id, groupName: g.name, capacity: (t as { defaultAllocation?: number }).defaultAllocation ?? 0 }));
  });
  return out;
}
